import { HttpEvent, HttpEventType } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TransferProgressEvent } from '@azure/core-rest-pipeline';
import { BlobApiEndpoint } from '@simOn/common/media';
import { UploadApiService } from '@simOn/common/upload-queue/data-access';
import { TQueueState, TUploadStatus } from '@simOn/common/upload-queue/models';
import { getBase64$ } from '@simOn/utils/file';
import { BehaviorSubject, catchError, filter, finalize, firstValueFrom, from, map, of, Subject, switchMap } from 'rxjs';

export const ErrorMessages: Record<string, string> = {
  InternalServerProblem: $localize`:@@UPLOAD_INTERNAL_ERROR:It’s impossible to upload file. Try again later.`
} as const;
@Injectable({ providedIn: 'root' })
export class UploadQueueService {
  private readonly _uploadApi = inject(UploadApiService);
  private readonly _queuedFilesState = new BehaviorSubject<TQueueState<TUploadStatus>[]>([]);
  private readonly _queuedChange = new Subject<TQueueState<TUploadStatus>>();
  readonly queuedFilesState$ = this._queuedFilesState.asObservable();
  readonly queuedFilesState = toSignal(this._queuedFilesState.asObservable());
  readonly queuedChanges$ = this._queuedChange.asObservable();
  readonly queuedChanges = toSignal(this._queuedChange.asObservable());

  clearQueueErrors() {
    const currentState = this._queuedFilesState.getValue().filter((element) => element.state === 'ERROR');
    currentState.forEach((element) => this._deleteFromQueue(element));
  }
  clearQueueUploaded() {
    const currentState = this._queuedFilesState.getValue().filter((element) => element.state === 'UPLOADED');
    currentState.forEach((element) => this._deleteFromQueue(element));
  }

  removeFromQueue(id: string): void {
    const element = this._queuedFilesState.getValue().find((qElement) => qElement.id === id);
    if (element) {
      element.cancel?.();
      this._deleteFromQueue(element);
    }
  }

  /**
   * Registers an operation whose upload is orchestrated externally (e.g. documents upload).
   * The caller drives it via `setOperationProgress` / `completeOperation` / `failOperation`.
   */
  registerOperation(file: File, type: 'photo' | 'other' = 'other'): string {
    const id = crypto.randomUUID();
    const element: TQueueState<'IN_PROGRESS'> = {
      id,
      progress: 0,
      name: file.name,
      dataUrl: undefined,
      size: file.size,
      state: 'IN_PROGRESS',
      type,
      attachMode: 'FromUpload'
    };
    this._addToQueue$(element);
    return id;
  }

  /**
   * Registers a grouped operation (e.g. a folder upload) shown as a single queue
   * element. The caller drives its stages via `setOperationStages` and finishes it
   * with `completeOperation` / `failOperation`.
   */
  registerGroupOperation(name: string): string {
    const id = crypto.randomUUID();
    const element: TQueueState<'IN_PROGRESS'> = {
      id,
      progress: 0,
      name,
      dataUrl: undefined,
      size: 0,
      state: 'IN_PROGRESS',
      type: 'other',
      attachMode: 'FromUpload',
      stagesDone: 0,
      stagesTotal: 0
    };
    this._addToQueue$(element);
    return id;
  }

  /**
   * Registers a download operation (e.g. packing documents into a ZIP) shown as a
   * single queue element. The caller drives it via `setOperationProgress` and ends it
   * with `completeDownloadOperation` / `failOperation`; `cancel` is invoked when the
   * user removes the element from the queue while the operation is still running.
   */
  registerDownloadOperation(name: string, cancel?: () => void): string {
    const id = crypto.randomUUID();
    const element: TQueueState<'IN_PROGRESS'> = {
      id,
      progress: 0,
      name,
      dataUrl: undefined,
      size: 0,
      state: 'IN_PROGRESS',
      type: 'other',
      attachMode: 'Unknown',
      cancel
    };
    this._addToQueue$(element);
    return id;
  }

  /**
   * Completes a download operation — unlike `completeOperation` it does not emit an
   * `UPLOADED` change, so attachment flows listening on `queuedChanges$` ignore it.
   */
  completeDownloadOperation(id: string): void {
    const element = this._queuedFilesState.getValue().find((qElement) => qElement.id === id);
    if (!element) return;
    this._deleteFromQueue(element);
  }

  /**
   * Registers an operation that already failed before its upload even started
   * (e.g. size or storage-limit validation) so it shows up as an ERROR element.
   */
  registerFailedOperation(
    name: string,
    size: number,
    error: string,
    type: 'photo' | 'other' = 'other',
    retry?: () => void
  ): string {
    const id = crypto.randomUUID();
    const element: TQueueState<'ERROR'> = {
      id,
      progress: 0,
      name,
      dataUrl: undefined,
      size,
      state: 'ERROR',
      type,
      attachMode: 'FromUpload',
      error,
      retry
    };
    this._addToQueue$(element);
    return id;
  }

  setOperationStages(id: string, stagesDone: number, stagesTotal: number, progress: number): void {
    const element = this._queuedFilesState.getValue().find((qElement) => qElement.id === id);
    if (!element) return;
    const updatedElement: TQueueState<'IN_PROGRESS'> = {
      ...element,
      stagesDone,
      stagesTotal,
      progress,
      state: 'IN_PROGRESS'
    };
    this._updateQueue$(updatedElement);
  }

  setOperationProgress(id: string, progress: number): void {
    const element = this._queuedFilesState.getValue().find((qElement) => qElement.id === id);
    if (!element) return;
    const updatedElement: TQueueState<'IN_PROGRESS'> = { ...element, progress, state: 'IN_PROGRESS' };
    this._updateQueue$(updatedElement);
  }

  completeOperation(id: string): void {
    const element = this._queuedFilesState.getValue().find((qElement) => qElement.id === id);
    if (!element) return;
    this._queuedChange.next({ ...element, progress: 100, state: 'UPLOADED', response: '' });
    this._deleteFromQueue(element);
  }

  failOperation(id: string, error?: string, retry?: () => void): void {
    const element = this._queuedFilesState.getValue().find((qElement) => qElement.id === id);
    if (!element) return;
    const updatedElement: TQueueState<'ERROR'> = {
      ...element,
      state: 'ERROR',
      error: error || ErrorMessages['InternalServerProblem'],
      retry
    };
    this._updateQueue$(updatedElement);
  }

  /** Removes the failed element from the queue and re-runs its operation via the caller-provided callback. */
  retryOperation(id: string): void {
    const element = this._queuedFilesState.getValue().find((qElement) => qElement.id === id);
    if (!element || element.state !== 'ERROR') return;
    this._deleteFromQueue(element);
    element.retry?.();
  }

  uploadFile(file: File, type: 'photo' | 'other' = 'photo', endpoint?: BlobApiEndpoint) {
    firstValueFrom(
      from(this._initElement(file, type)).pipe(
        switchMap((id) =>
          this._uploadApi
            .sendFileToBlob(file, endpoint, (_reportProgress) => this._reportProgress(_reportProgress, id))
            .pipe(
              map((response) => {
                if (!response) throw Error('Upload failure');
                const element = this._queuedFilesState.getValue().find((qElement) => qElement.id === id)!;
                const updatedElement: TQueueState<'UPLOADED'> = {
                  ...element,
                  response,
                  state: 'UPLOADED'
                };
                this._updateQueue$(updatedElement);
                return updatedElement;
              }),
              catchError((error) => {
                const element = this._queuedFilesState.getValue().find((qElement) => qElement.id === id)!;
                const updatedElement: TQueueState<'ERROR'> = {
                  ...element,
                  state: 'ERROR',
                  error: ErrorMessages[error.ErrorCode] || ErrorMessages['InternalServerProblem']
                };

                this._updateQueue$(updatedElement);
                return of(updatedElement);
              }),
              filter((element) => element.state == 'UPLOADED' || element.state == 'ERROR'),
              finalize(() => this.clearQueueUploaded())
            )
        )
      )
    );
  }
  private _reportProgress(_reportProgress: TransferProgressEvent, id: string): void {
    const element = this._queuedFilesState.getValue().find((qElement) => qElement.id === id);
    if (!element) throw Error('Element does not exist in queue');

    const progress = this._getPercentageProgress(element.size, _reportProgress.loadedBytes);
    const updatedElement: TQueueState<'IN_PROGRESS'> = {
      ...element,
      progress,
      state: 'IN_PROGRESS'
    };
    this._updateQueue$(updatedElement);
  }
  private async _initElement(file: File, type: 'photo' | 'other'): Promise<string> {
    const id = crypto.randomUUID();
    this.clearQueueErrors();
    const dataUrl = (await firstValueFrom(getBase64$(file))).target?.result;
    const element: TQueueState<'IN_PROGRESS'> = {
      id,
      progress: 0,
      name: file.name,
      dataUrl,
      size: file.size,
      state: 'IN_PROGRESS',
      type,
      attachMode: 'FromUpload'
    };
    firstValueFrom(this._addToQueue$(element));
    this._queuedChange.next(element);
    return id;
  }

  private _addToQueue$(queueElement: TQueueState<TUploadStatus>) {
    const currentState = this._queuedFilesState.getValue();
    this._queuedFilesState.next([...currentState, queueElement]);
    this._queuedChange.next({ ...queueElement });
    return this._queuedFilesState.asObservable();
  }

  private _updateQueue$(queueElement: TQueueState<TUploadStatus>) {
    const currentState = this._queuedFilesState.getValue().filter((qElement) => qElement.id !== queueElement.id);
    this._queuedFilesState.next([...currentState, queueElement]);
    this._queuedChange.next({ ...queueElement });
    return this._queuedFilesState.asObservable();
  }
  private _deleteFromQueue(queueElement: TQueueState<TUploadStatus>) {
    const currentState = this._queuedFilesState.getValue().filter((qElement) => qElement.id !== queueElement.id);
    this._queuedFilesState.next([...currentState]);
  }

  private _getPercentageProgress = (size: number, loaded: number) => Math.floor((loaded / size) * 100);

  private _getActionFromHttpEvent$(event: HttpEvent<any>, elementId: string) {
    const element = this._queuedFilesState.getValue().find((qElement) => qElement.id === elementId);
    if (!element) throw Error('Element does not exist in queue');
    switch (event.type) {
      case HttpEventType.UploadProgress:
        const progress = this._getPercentageProgress(element.size, event.loaded);
        const updatedElement: TQueueState<'IN_PROGRESS'> = {
          ...element,
          progress,
          state: 'IN_PROGRESS'
        };
        this._updateQueue$(updatedElement);
        return of(updatedElement);

      case HttpEventType.ResponseHeader: {
        const updatedElement: TQueueState<'IN_PROGRESS'> = {
          ...element,
          progress: 100,
          state: 'IN_PROGRESS'
        };
        this._updateQueue$(updatedElement);
        return of(updatedElement);
      }
      case HttpEventType.Response: {
        if (event.status === 200) {
          const updatedElement: TQueueState<'UPLOADED'> = {
            ...element,
            progress: 100,
            state: 'UPLOADED',
            response: event.body
          };
          this._updateQueue$(updatedElement);
          return of(updatedElement);
        } else {
          const updatedElement: TQueueState<'ERROR'> = {
            ...element,
            progress: 0,
            error: event.statusText,
            state: 'ERROR'
          };

          this._updateQueue$(updatedElement);
          return of(updatedElement);
        }
      }
      default:
        return of(element);
    }
  }
}
